import React, { createContext, useContext, useReducer } from "react";

import { AppDispatch, appStateReducer } from "./actions";
import { AppState } from "./types";
import { action, PlaceData, MoveData, serverAction } from "../types/game";
import { game } from "../game/config";
import { startGame, movePieceServer } from "../server/game";

interface AppContext {
  state: AppState;
  dispatch: AppDispatch;
  startOnlineGame: () => Promise<boolean>;
  movePiece: (move: action) => void;
  executeServerAction: (action: serverAction) => void;
}

const initialAppState: AppState = {
  game: game(["player1", "player2"], null, null),
  onGoingGame: false,
  displayGameResults: false,
};

export const appContext = createContext<AppContext | null>(null);

// Context provider
export const AppStateProvider = ({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement => {
  const [state, dispatch] = useReducer(appStateReducer, initialAppState);

  const startOnlineGame = async (): Promise<boolean> => {
    if (!state.userUuid) {
      throw new Error("cannot identify player");
    }

    return startGame(state.userUuid).then((res) => {
      console.log("startGame response:", res);
      if ("failed" in res.data) {
        console.log("res.data.failed:", res.data.failed);
        return false;
      }
      const responseData = res.data;
      console.log("create game response data:", responseData);

      dispatch({
        type: "INITIALIZE_GAME",
        payload: {
          players: ["White", "Black"],
          onlineGameId: res.data.gameId,
          localPlayerId: res.data.localPlayerId,
        },
      });

      return true; // TODO: IMPORTANT! handle if failed to create match
    });
  };

  const executeServerAction = (action: serverAction) => {
    console.log("executing server action");
    if (action.type === "place") {
      state.game.board.placePiece(action.pieceKind, action.owner, action.hex);
      dispatch({
        type: "NEXT_TURN",
        payload: { justPlaced: action.pieceKind },
      });
    } else if (action.type === "move") {
      state.game.board.movePiece(action.piece, action.to);
      dispatch({ type: "NEXT_TURN", payload: { justPlaced: undefined } });
    }
  };

  const movePiece = (action: action, offlineOnly?: true) => {
    let moveData: PlaceData | MoveData | null = null;
    let executeOffline: () => void = () => {
      throw new Error("failed to place/move piece offline");
    };

    // If move
    if (action.type === "move") {
      const piece = state.game.action.moving;
      if (!piece) {
        throw new Error("no piece selected to move here");
      }
      if (!state.userUuid) {
        throw new Error("cannot identify player");
      }

      if (!offlineOnly && state.game.onlineGameId) {
        const localPlayerId = state.game.localPlayerId;
        if (localPlayerId === null) {
          throw new Error("online game but no local player id");
        }
        moveData = {
          game: state.game.onlineGameId,
          byPlayer: localPlayerId,
          type: "move",
          playerId: state.userUuid,
          piece: piece.valueOf(),
          to: action.to.valueOf(),
        };
      }

      executeOffline = () => {
        state.game.board.movePiece(piece, action.to);
        dispatch({ type: "NEXT_TURN", payload: { justPlaced: undefined } });
      };
      // If place
    } else if (action.type === "place") {
      const placing = state.game.action.placing;
      if (!placing) {
        console.warn("no piece selected to place here");
        return;
      }

      if (!offlineOnly && state.game.onlineGameId) {
        const localPlayerId = state.game.localPlayerId;
        if (localPlayerId === null) {
          throw new Error("online game but no local player id");
        }
        moveData = {
          game: state.game.onlineGameId,
          byPlayer: localPlayerId,
          type: "place",
          hex: action.hex.valueOf(),
          kind: action.justPlaced,
        };
      }

      // Function to place offline
      executeOffline = () => {
        state.game.board.placePiece(
          placing,
          state.game.turnOfPlayer,
          action.hex
        );
        dispatch({
          type: "NEXT_TURN",
          payload: { justPlaced: action.justPlaced },
        });
      };
    }

    // console.log("moving:", piece.kind);

    // Propagate move to database first if game is online
    if (!offlineOnly && state.game.onlineGameId) {
      console.log("sending move to firebase function");
      if (!moveData) {
        throw new Error("failed to build move data");
      }
      movePieceServer(moveData).then((res: any) => {
        console.log("network response:", res);
        executeOffline();
      });
    } else {
      console.log("moving in offline mode");
      executeOffline();
    }
  };

  return (
    <appContext.Provider
      value={{
        state,
        dispatch,
        startOnlineGame,
        movePiece,
        executeServerAction,
      }}
    >
      {children}
    </appContext.Provider>
  );
};

// Custom hook to access state and dispatch
export function useApp(): AppContext {
  const context = useContext(appContext);
  if (!context) {
    throw new Error("appContext was not initialized");
  }
  return context;
}
